import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { supabase } from '@/integrations/supabase/client';

export interface BookAvatar {
  id: string;
  book_title: string;
  book_id: number | null;
  image_url: string;
  display_order: number;
  created_at: string;
  updated_at: string;
}

export interface BookAvatarGroup {
  bookTitle: string;
  bookId: number | null;
  avatars: BookAvatar[];
}

export const useBookAvatars = () => {
  const queryClient = useQueryClient();

  const { data: avatars = [], isLoading } = useQuery({
    queryKey: ['book-avatars'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('book_avatars')
        .select('*')
        .order('book_title', { ascending: true })
        .order('display_order', { ascending: true });

      if (error) throw error;
      return data as BookAvatar[];
    },
  });

  const groupedAvatars: BookAvatarGroup[] = avatars.reduce((acc, avatar) => {
    const existing = acc.find(g => g.bookTitle === avatar.book_title);
    if (existing) {
      existing.avatars.push(avatar);
    } else {
      acc.push({
        bookTitle: avatar.book_title,
        bookId: avatar.book_id,
        avatars: [avatar],
      });
    }
    return acc;
  }, [] as BookAvatarGroup[]);

  const uploadAvatarMutation = useMutation({
    mutationFn: async ({ 
      bookTitle, 
      bookId, 
      imageUrl 
    }: { 
      bookTitle: string; 
      bookId: number | null; 
      imageUrl: string 
    }) => {
      const { data, error } = await supabase
        .from('book_avatars')
        .insert({
          book_title: bookTitle,
          book_id: bookId,
          image_url: imageUrl,
        })
        .select()
        .single();

      if (error) throw error;
      return data;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['book-avatars'] });
    },
  });

  const deleteAvatarMutation = useMutation({
    mutationFn: async (avatarId: string) => {
      const { error } = await supabase
        .from('book_avatars')
        .delete()
        .eq('id', avatarId);

      if (error) throw error;
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['book-avatars'] });
    },
  });

  const getRandomAvatar = (): string | null => {
    if (avatars.length === 0) return null;
    const randomIndex = Math.floor(Math.random() * avatars.length);
    return avatars[randomIndex].image_url;
  };

  return {
    avatars,
    groupedAvatars,
    isLoading,
    uploadAvatar: uploadAvatarMutation.mutate,
    deleteAvatar: deleteAvatarMutation.mutate,
    getRandomAvatar,
  };
};
